<template>
	<section ref="sectionRef"
			 class="flex-module container-fluid flexible-iconlist">
		<div class="row">
			<div class="col-xs-12">
				<h2 class="section-title">
					{{ data.title }}
				</h2>
			</div>
		</div>
		<div class="row">
			<div class="col-xs-12 col-lg-10 col-lg-offset-1 col-xl-10 col-xl-offset-1">
				<ul class="nolist">
					<li v-for="(item, index) in data.item.items"
						:key="item.id">
						<div class="list-item">
							<div :id="'accordion-label-' + index"
								 ref="itemRefs"
								 class="toggler accordion-title"
								 role="button"
								 data-cursor-hover
								 @click.prevent="toggleItem($event, index)"
								 @mouseenter="$event => hoverOn($event)"
								 @mouseleave="$event => hoverOff($event)">
								<div class="arrow">
									<span class="line" />
									<arrowTip class="point" />
								</div>
								<h5 v-if="item.title"
									class="list-title">
									<span>{{ item.title }}</span>
								</h5>
							</div>
							<div class="accordion-body">
								<div class="accordion-content"
									 v-html="item.body_text" />
							</div>
						</div>
					</li>
				</ul>
			</div>
		</div>
	</section>
</template>

<script setup>
import arrowTip from '~/assets/images/icon-caret-right.svg'

defineProps({
	data: {
		type: Object,
		required: true
	}
})

const { gsap } = useGsap()

const sectionRef = ref(null)

const itemRefs = ref([])
let $q

function toggleItem(el) {
	const clickedTarget = el.currentTarget
	const clickedAccordion = clickedTarget.nextElementSibling
	const allItems = $q('.list-item')

	allItems.forEach(item => {
		const looped = gsap.utils.selector(item)
		const loopedAccordion = looped('.accordion-body')[0]
		const loopedTitle = looped('.list-title')
		const loopedArrow = looped('.arrow')

		if (loopedAccordion !== clickedAccordion) {
			// close other accordions
			gsap
				.timeline()
				.to(loopedArrow, { x: '-2em', duration: 0.20 })
				.to(loopedTitle, { marginLeft: 0, duration: 0.20 }, '<')

			gsap.to(loopedAccordion, {
				height: 0,
				duration: 0.25,
				ease: 'power2.out',
			})

			if (looped('.active').length) {
				looped('.active')[0].classList.remove('active')
			}

		} else {
			const clicked = gsap.utils.selector(clickedTarget)
			const clickedTitle = clicked('.list-title')
			const clickedArrow = clicked('.arrow')

			if (clickedAccordion.style.height === '0px' || clickedAccordion.style.height === '') {
				// open clicked accordion
				clickedTarget.classList.add('active')

				gsap
					.timeline()
					.to(clickedArrow, { x: 0, ease: 'power1.out', duration: 0.20 })
					.to(clickedTitle, { marginLeft: '2em', ease: 'power1.out', duration: 0.20 }, '<')

				gsap.to(clickedAccordion, {
					height: clickedAccordion.scrollHeight,
					duration: 0.25,
					overwrite: true,
					ease: 'power2.out',
				})
			} else {
				// close clicked accordion
				clickedTarget.classList.remove('active')

				gsap
					.timeline()
					.to(clickedArrow, { x: '-2em', duration: 0.20 })
					.to(clickedTitle, { marginLeft: 0, duration: 0.20 }, '<')

				gsap.to(clickedAccordion, {
					height: 0,
					duration: 0.25,
					overwrite: true,
					ease: 'power2.out',
				})
			}
		}
	})
}

function hoverOn(el) {
	if (el.target.classList.contains('active')) return

	const clicked = gsap.utils.selector(el.target)
	const clickedTitle = clicked('.list-title')
	const clickedArrow = clicked('.arrow')

	gsap
		.timeline()
		.to(clickedArrow, { x: 0, ease: 'power1.out', duration: 0.20 })
		.to(clickedTitle, { marginLeft: '2em', ease: 'power1.out', duration: 0.20 }, '<')
}

function hoverOff(el) {
	if (el.target.classList.contains('active')) return

	const clicked = gsap.utils.selector(el.target)
	const clickedTitle = clicked('.list-title')
	const clickedArrow = clicked('.arrow')

	gsap
		.timeline()
		.to(clickedArrow, { x: '-2em', duration: 0.20 })
		.to(clickedTitle, { marginLeft: 0, duration: 0.20 }, '<')
}

function initAnims() {
	$q = gsap.utils.selector(sectionRef.value)

	gsap
		.timeline({ onComplete: () => itemRefs.value[0].classList.add('active') })
		.to(itemRefs.value[0].nextElementSibling, {
			height: itemRefs.value[0].nextElementSibling.scrollHeight,
			duration: 0.25,
			overwrite: true,
			ease: 'power2.out',
		})
}

onMounted(() => {
	useAnims(initAnims)
})

</script>

<style lang="scss" scoped>
.summary-wrapper {
	display: flex;
	gap: rem-calc($gutter-width);
	flex-flow: column-reverse nowrap;
	// margin-bottom: rem-calc(50);

	@include for-devices-above(lg) {
		flex-flow: column nowrap;
	}

	.video-wrapper {
		max-width: rem-calc(120);

		@include for-devices-above(lg) {
			max-width: rem-calc(190);
		}

		video {
			height: auto;
			margin-bottom: rem-calc(60);
		}
	}

	p {
		@include textBody;
	}
}

ul {
	@include for-devices-above(lg) {
		margin-top: rem-calc(-50);
	}


	li {
		overflow: hidden;

		.accordion-title {
			position: relative;

			.arrow {
				position: absolute;
				left: 0;
				top: calc(50%);
				transform: translate(-2em, -50%);

				.line {
					background-color: $black;
				}

				.point {
					position: absolute;
					color: $black;
					// transform: translate(-82%, -50%);
				}
			}

			h5 {
				padding: rem-calc(20 0 20 0);

				@include for-devices-above(sm) {
					padding: rem-calc(30 0 30 0);
				}

				@include for-devices-above(lg) {
					padding: rem-calc(40 0 40 0);
				}

				@include textH6;
				margin: 0;
				will-change: margin-left;

				.number {
					margin-right: 1em;
				}
			}
		}

		&:first-of-type {
			.arrow {
				transform: translate(0, -50%);
			}

			h5 {
				margin-left: 2em;
			}

			.accordion-body {
				height: auto;
			}
		}

		.accordion-body {
			height: 0;
			overflow: hidden;
			border-bottom: 4px solid $black;

			@include for-devices-below(lg) {
				border-bottom: 3px solid $black;
			}

			@include for-devices-below(md) {
				border-bottom: 2px solid $black;
			}

			.accordion-content {
				@include textBody;
				padding-bottom: rem-calc(40);
			}
		}
	}
}
</style>